import React, {Fragment, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import Icon from '@/shared/components/Icon';

interface Props {
    open: boolean;
    programName: string;
    bodyText?: string | null;
    confirmLabel?: string | null;
    onConfirm: () => Promise<void>;
    onClose: () => void;
}

/**
 * Two-step enrollment confirmation. Healthcare UX standard — patients
 * shouldn't enroll on a single accidental click. Shows the program
 * name + the BioMatch's `cta_body_text` so the patient knows exactly
 * what they're signing up for. Confirm spinner is in-component so the
 * caller doesn't have to thread loading state.
 */
const EnrollConfirmModal: React.FC<Props> = ({
    open,
    programName,
    bodyText,
    confirmLabel,
    onConfirm,
    onClose,
}) => {
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={submitting ? () => undefined : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-start justify-between gap-3">
                                    <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                        Enroll in {programName}?
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        aria-label="Close"
                                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50">
                                        <Icon name="xmark" className="h-4 w-4" />
                                    </button>
                                </div>

                                {bodyText && (
                                    <p className="mt-3 text-sm leading-relaxed text-mld-text-secondary">
                                        {bodyText}
                                    </p>
                                )}

                                <p className="mt-3 text-xs text-mld-text-secondary">
                                    Confirming will record your enrollment. Our team will reach out with next steps.
                                </p>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        className="rounded-full border border-mld-panel-border px-4 py-2 text-sm font-medium text-mld-text-primary hover:bg-mld-panel-bg disabled:opacity-50">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirm}
                                        disabled={submitting}
                                        className="inline-flex items-center gap-2 rounded-full bg-mld-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                                        {submitting && <Icon name="spinner" spin className="h-4 w-4" />}
                                        {confirmLabel || 'Confirm enrollment'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default EnrollConfirmModal;
